import { useEffect, useRef, useState } from 'react';
import { Play } from 'lucide-react';
import type { PostVideo } from '@/lib/postImage';

interface PostMediaProps {
  /** Static image URL (used when there is no video, or as a video fallback). */
  image?: string;
  /** Video to render as a looping, muted thumbnail. */
  video?: PostVideo;
  /** Alt text / accessible label. */
  alt: string;
}

/**
 * Renders a post's featured media inside a 16:10 frame.
 * If the post has a video, it plays muted, looped, and inline — but only
 * while on-screen (to save bandwidth) and only when motion is allowed.
 * Falls back to a static image (poster or provided image) otherwise.
 */
export function PostMedia({ image, video, alt }: PostMediaProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [videoErrored, setVideoErrored] = useState(false);

  const showVideo = !!video && !videoErrored;
  const poster = video?.poster || image;

  // Play only while visible; pause when scrolled away.
  useEffect(() => {
    if (!showVideo) return;
    const el = videoRef.current;
    const container = containerRef.current;
    if (!el || !container) return;

    // Respect reduced-motion: never autoplay.
    const prefersReduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    if (prefersReduced) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            el.play().catch(() => {
              /* autoplay can be blocked; ignore */
            });
          } else {
            el.pause();
          }
        }
      },
      { threshold: 0.25 },
    );

    observer.observe(container);
    return () => observer.disconnect();
  }, [showVideo]);

  return (
    <div
      ref={containerRef}
      className="relative aspect-[16/10] w-full overflow-hidden bg-muted"
    >
      {showVideo ? (
        <>
          <video
            ref={videoRef}
            src={video!.url}
            poster={poster}
            muted
            loop
            playsInline
            preload="metadata"
            onError={() => setVideoErrored(true)}
            className="h-full w-full object-cover transition-transform duration-500 motion-safe:group-hover:scale-105"
            aria-label={alt}
          />
          {/* Video indicator badge */}
          <div className="pointer-events-none absolute bottom-2 right-2 flex items-center gap-1 rounded-full bg-black/55 px-2 py-1 text-white backdrop-blur-sm">
            <Play className="size-3.5 fill-current" />
            <span className="text-[11px] font-medium uppercase tracking-wide">
              Video
            </span>
          </div>
        </>
      ) : (
        poster && (
          <img
            src={poster}
            alt={alt}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 motion-safe:group-hover:scale-105"
          />
        )
      )}
    </div>
  );
}
